import React, { useState } from 'react';
import { authenticateYouTube, isYouTubeAuthenticated } from '../services/youtubeAuthService';
import { AlertTriangle, Lock } from 'react-feather';

// YouTube reply service - placeholder implementation
const postCommentReply = async (commentId, replyText) => {
  // TODO: Re-implement youtubeReplyService
  throw new Error('YouTube reply service is not yet implemented');
};

const ReplyForm = ({ commentId, onReplyPosted, onCancel, initialText = '' }) => {
  const [replyText, setReplyText] = useState(initialText || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(isYouTubeAuthenticated());

  // Listen for AI-generated reply text
  React.useEffect(() => {
    // Set up function for AI to call
    window.setAIReplyText = (text) => {
      setReplyText(text);
    };
    
    return () => {
      delete window.setAIReplyText;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) {
      setError('Please enter a reply');
      return;
    }

    if (!isAuthenticated) {
      setError('Please authenticate with YouTube first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reply = await postCommentReply(commentId, replyText);
      // Success! Clear any previous errors
      setError(null);
      setReplyText('');
      if (onReplyPosted) {
        onReplyPosted(reply);
      }
    } catch (err) {
      console.error('Reply submission error:', err);
      if (err.message?.includes('Not authenticated') || err.message?.includes('Authentication expired')) {
        setIsAuthenticated(false);
      }
      // Only show error if it's a real error (not a warning)
      setError(err.message || 'Failed to post reply. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await authenticateYouTube();
      // Token is already stored in localStorage by authenticateYouTube
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.message || 'Failed to authenticate with YouTube');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="mt-4 p-5 bg-gray-50 rounded-lg border border-gray-200 text-center">
        <p className="m-0 mb-4 text-gray-600 text-sm">
          You need to authorize this app to post replies on YouTube.
        </p>
        <button
          onClick={handleAuthenticate}
          disabled={loading}
          className="px-6 py-3 bg-gradient-to-r from-error-600 to-error-800 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 mb-3 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Authenticating...' : <><Lock size={16} className="inline mr-1" /> Authorize with YouTube</>}
        </button>
        {error && (
          <div className="bg-warning-light border border-warning-base text-warning-dark px-3 py-2.5 rounded-md mb-3 flex items-center gap-2 text-sm">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}
        {onCancel && (
          <button onClick={onCancel} className="block mx-auto mt-3 px-4 py-2 bg-transparent border border-gray-300 rounded-md text-gray-600 text-sm cursor-pointer transition-all hover:bg-gray-100 hover:border-gray-400">
            Cancel
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {error && (
        <div className="bg-warning-light border border-warning-base text-warning-dark px-3 py-2.5 rounded-md mb-3 flex items-center gap-2 text-sm">
          <span className="text-base">ÔÜá´©Å</span>
          <span>{error}</span>
        </div>
      )}
      
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        placeholder="Write your reply..."
        className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg text-sm font-sans resize-y min-h-20 mb-3 transition-colors focus:outline-none focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        disabled={loading}
        rows={3}
        maxLength={10000}
      />
      
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="text-xs text-gray-500">
          {replyText.length}/10,000
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 bg-gray-200 text-gray-800 border-none rounded-md text-sm font-semibold cursor-pointer transition-all hover:bg-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white border-none rounded-md text-sm font-semibold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={loading || !replyText.trim()}
          >
            {loading ? 'Posting...' : 'Post Reply'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ReplyForm;

